import { RevolveFeatureDefinitionModel } from '../../../definition/revolve/RevolveFeatureDefinition';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { BossRevolveFeaturePrototype } from '../../../prototype/revolve/BossRevolveFeaturePrototype';
import { CutRevolveFeaturePrototype } from '../../../prototype/revolve/CutRevolveFeaturePrototype';
import { resolveSketchProfileSourceSketchId } from '../../../../utils/feature/resolveSketchProfileSourceSketchId';
import { FeaturePreviewExecutionHandler, type FeaturePreviewExecutionInput, type FeaturePreviewExecutionResult } from '../FeaturePreviewExecutionHandler';
import { RevolvePreviewExecution } from './RevolvePreviewExecution';

/**
 * 旋转特征预览执行处理器。
 */
export class RevolvePreviewExecutionHandler extends FeaturePreviewExecutionHandler {
    /** 旋转预览执行器。 */
    private readonly execution: RevolvePreviewExecution;

    /**
     * @param execution 旋转预览执行器。
     */
    public constructor(execution: RevolvePreviewExecution) {
        super();
        this.execution = execution;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeaturePreviewExecutionInput): boolean {
        return (input.featureKind === 'boss-revolve' || input.featureKind === 'cut-revolve')
            && input.definition instanceof RevolveFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当预览输入中的定义不是旋转特征定义时。
     */
    public async execute(input: FeaturePreviewExecutionInput): Promise<FeaturePreviewExecutionResult> {
        if (!(input.definition instanceof RevolveFeatureDefinitionModel)) {
            throw new Error('旋转预览处理器收到的 definition 类型不正确');
        }
        const PrototypeCtor = input.featureKind === 'cut-revolve'
            ? CutRevolveFeaturePrototype
            : BossRevolveFeaturePrototype;
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: input.definition.getSourceSketchId(),
            bindings: input.bindings,
        });
        const prototype = new PrototypeCtor({
            featureId: input.featureId ?? `preview:${Date.now()}`,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId,
        });
        const previewResult = input.session
            ? await this.execution.executeEditPreview(
                input.session as FeatureEditSession<RevolveFeatureDefinitionModel>,
                input.definition,
            )
            : await this.execution.executeCreatePreview(input.definition);
        return {
            meshData: previewResult.meshData,
            presentation: null,
            prototype,
            draftDefinition: input.definition,
            fragmentMeshes: previewResult.fragmentMeshes,
        };
    }
}
